import { useState, useEffect, useRef, useCallback } from 'react';
import { Database, ChevronDown, Check, X, RefreshCw, Sparkles } from 'lucide-react';
import { apiRequest } from '@/lib/api';
import DatabaseConnectionModal from './DatabaseConnectionModal';

interface DatabaseDropdownProps {
  projectId: string;
}

interface ConnectionStatus {
  connected: boolean;
  status: string;
  type?: string;
  lastTested?: string;
  error?: string;
}

type Mode = 'choose' | 'create-new' | 'manual-entry' | 'setup-pat';

export default function DatabaseDropdown({ projectId }: DatabaseDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialMode, setModalInitialMode] = useState<Mode | undefined>(undefined);
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const checkStatus = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const response = await apiRequest<ConnectionStatus>('get', `/database/status/${projectId}`);
      setStatus(response);
    } catch (error) {
      setStatus({ connected: false, status: 'not_configured' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDisconnect = async () => {
    try {
      await apiRequest('delete', `/database/connections/${projectId}`);
      setStatus({ connected: false, status: 'not_configured' });
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleConnectionSaved = () => {
    checkStatus();
  };

  const handleOpenModal = (mode?: Mode) => {
    setModalInitialMode(mode);
    setIsModalOpen(true);
    setIsOpen(false);
  };

  const isConnected = status?.connected ?? false;
  const statusColor = isConnected ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
      >
        <Database size={16} />
        <span>Database</span>
        <div className={`w-2 h-2 rounded-full ${statusColor} ${isLoading ? 'animate-pulse' : ''}`} />
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
          {/* Status Section */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-400">Status</span>
              <div className="flex items-center gap-2">
                {isConnected ? (
                  <>
                    <Check size={14} className="text-green-500" />
                    <span className="text-xs text-green-500">Connected</span>
                  </>
                ) : (
                  <>
                    <X size={14} className="text-red-500" />
                    <span className="text-xs text-red-500">Not Connected</span>
                  </>
                )}
              </div>
            </div>

            {status?.type && (
              <div className="mt-2 text-xs text-gray-400">
                Type: <span className="text-white capitalize">{status.type}</span>
              </div>
            )}

            {status?.error && (
              <div className="mt-2 text-xs text-red-400">{status.error}</div>
            )}
          </div>

          {/* Actions */}
          <div className="py-1">
            {!isConnected ? (
              <>
                <button
                  onClick={() => handleOpenModal('create-new')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <Sparkles size={14} className="text-green-400" />
                  Create Database
                </button>
                <button
                  onClick={() => handleOpenModal(undefined)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                >
                  Connect Database
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleOpenModal(undefined)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                >
                  Edit Connection
                </button>

                <button
                  onClick={checkStatus}
                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <RefreshCw size={14} />
                  Test Connection
                </button>

                <button
                  onClick={handleDisconnect}
                  className="w-full px-4 py-2 text-left text-sm text-red-400 hover:text-red-300 hover:bg-gray-800 transition-colors"
                >
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      <DatabaseConnectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectId={projectId}
        onConnectionSaved={handleConnectionSaved}
        initialMode={modalInitialMode}
      />
    </div>
  );
}
